import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Presentation, Activity, MessageSquare, Wrench, Workflow, ExternalLink, FileText, Download, Compass, Play, Leaf, Building2, Scale, Users2, Handshake, GraduationCap, HeartPulse, Briefcase, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export const ImpactPage = () => {
  return (
    <div id="main-content" className="pt-20" data-testid="impact-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-slate-900 text-white relative overflow-hidden">
        {/* Layered gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950" />
        <div
          className="absolute inset-0 opacity-50"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 25% 40%, rgba(59,130,246,0.18), transparent 60%), radial-gradient(ellipse 60% 50% at 80% 70%, rgba(139,92,246,0.12), transparent 60%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="overline text-blue-300 mb-4">Impact</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-6">
              Our Impact — A Global Transformation Underway
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed">
              The Centre's work sits at a transformative moment: the convergence of neurotechnology, artificial intelligence, consciousness studies, and fundamental physics is reshaping our understanding of mind, intelligence, and reality itself. Through partnerships, publications, and cross-sector dialogue, the Centre is helping to shape how this transformation unfolds for research, policy, ethics, and the future of humanity.
            </p>
          </div>
        </div>
      </section>


      {/* Partnership in Action: BCI & Neurotechnology Spring School 2026 */}
      <section className="py-16 lg:py-24 bg-white" data-testid="spring-school-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <p className="overline text-brand-primary mb-4">Partnership in Action</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Centre for Consciousness Studies · Spring School 2026
            </h2>
            <p className="text-slate-600 leading-relaxed">
              The Centre for Consciousness Studies, in collaboration with the{" "}
              <span className="font-semibold text-slate-900">BCI &amp; Neurotechnology Spring School 2026</span> and the{" "}
              <span className="font-semibold text-slate-900">Global BCI Network</span>, is serving as an official host of one of the world's largest interdisciplinary neurotechnology learning programs.
            </p>
          </div>

          {/* Official host poster + intro */}
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center mb-16">
            <div className="lg:col-span-2">
              <div className="rounded-xl overflow-hidden shadow-lg ring-1 ring-slate-200">
                <img
                  src="/collaborations/spring-school-2026/official-host.jpg"
                  alt="Official Host — BCI & Neurotechnology Spring School 2026 — Centre for Consciousness Studies (AU), April 20–29, 2026"
                  className="w-full h-auto block"
                  loading="lazy"
                  data-testid="spring-school-poster"
                />
              </div>
            </div>
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 text-sm font-medium text-brand-primary mb-4">
                <Calendar className="w-4 h-4" />
                <span>20 – 29 April 2026 · 10 days · Online + Hands-on</span>
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl text-slate-900 mb-4 leading-snug">
                A global learning experience at the intersection of brain, machine, and consciousness
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Last year, more than <span className="font-semibold text-slate-900">90,000 participants</span> from over{" "}
                <span className="font-semibold text-slate-900">140 countries</span> joined this global program. This year, the Spring School once again offers an extraordinary opportunity to{" "}
                <span className="font-medium text-slate-900">learn, engage, and collaborate</span> across disciplines — from foundational neuroscience to real-world clinical applications.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  asChild
                  size="lg"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="spring-school-register-btn"
                >
                  <a
                    href="https://www.gtec.at/event/spring-school-program-2026/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Register for the Program <ExternalLink className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* What participants gain */}
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-12 mb-12">
            <h3 className="font-serif text-2xl text-slate-900 mb-8 text-center">
              Over 10 days, participants will gain access to:
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {[
                {
                  icon: Presentation,
                  title: "100+ Expert Lectures",
                  body: "From leading researchers and clinicians working at the frontier of BCI and neurotechnology.",
                },
                {
                  icon: Activity,
                  title: "Live BCI Demonstrations",
                  body: "Real-time demonstrations of BCI systems and clinical neurotechnology platforms.",
                },
                {
                  icon: MessageSquare,
                  title: "Interactive Discussions",
                  body: "Opportunities for direct questions, dialogue, and cross-institutional collaboration.",
                },
                {
                  icon: Wrench,
                  title: "BR41N.IO Hackathon",
                  body: "Hands-on development through the global BR41N.IO Hackathon ecosystem.",
                },
                {
                  icon: Workflow,
                  title: "Signal → Clinic Journey",
                  body: "A full journey from signal acquisition through processing to clinical application.",
                },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="bg-white rounded-xl p-6 border border-slate-200">
                    <div className="w-10 h-10 rounded-lg bg-brand-highlight flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-brand-primary" />
                    </div>
                    <h4 className="font-serif text-lg text-slate-900 mb-2 leading-snug">
                      {item.title}
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.body}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Event photo gallery */}
          <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
            {[
              {
                src: "/collaborations/spring-school-2026/neurotech-discussion.jpg",
                alt: "Researchers in discussion at the Neurotechnology Summit wearing g.tec EEG caps under a Neuro Rehabilitation banner",
                caption: "Cross-disciplinary dialogue · Neurotechnology Summit",
              },
              {
                src: "/collaborations/spring-school-2026/bci-demo.jpg",
                alt: "Hands-on BCI demonstration with a participant wearing a g.GAMMAcap EEG headset operating a laptop with live signal visualization",
                caption: "Hands-on BCI system · live signal acquisition",
              },
              {
                src: "/collaborations/spring-school-2026/neurotech-showcase.jpg",
                alt: "Neurotechnology showcase by g.tec at the Neurotechnology Summit",
                caption: "Neurotechnology showcase · global research network",
              },
            ].map((p, idx) => (
              <figure
                key={idx}
                className="rounded-xl overflow-hidden shadow-md ring-1 ring-slate-200 bg-white group"
                data-testid={`spring-school-photo-${idx}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <figcaption className="text-xs text-slate-500 px-4 py-3 border-t border-slate-100">
                  {p.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Neurotechnology Summit 2025 */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="neurotech-summit-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <p className="overline text-brand-primary mb-4">Cross-Sector Collaboration</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Neurotechnology Summit 2025
            </h2>
            <p className="text-slate-600 leading-relaxed">
              The Centre for Consciousness Studies, together with many other leaders collaborating through the{" "}
              <span className="font-semibold text-slate-900">Neurotechnology Summit 2025</span> —{" "}
              <span className="font-semibold text-slate-900">Australia's leading neurotechnology platform</span> — contributed to an authoritative synthesis of the key themes, insights, and points of tension explored across two days of cross-sector dialogue.
            </p>
          </div>

          {/* Featured paper download card */}
          <div className="bg-white rounded-2xl shadow-lg ring-1 ring-slate-200 overflow-hidden mb-12">
            <div className="grid lg:grid-cols-5">
              {/* Paper mock cover */}
              <div className="lg:col-span-2 relative bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 p-8 lg:p-12 flex items-center justify-center min-h-[280px]">
                <div
                  className="absolute inset-0 opacity-40"
                  style={{
                    background:
                      "radial-gradient(ellipse 70% 60% at 30% 40%, rgba(59,130,246,0.25), transparent 60%), radial-gradient(ellipse 60% 50% at 80% 70%, rgba(139,92,246,0.18), transparent 60%)",
                  }}
                />
                <div
                  className="absolute inset-0 opacity-[0.08]"
                  style={{
                    backgroundImage:
                      "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "40px 40px",
                  }}
                />
                <div className="relative text-center">
                  <FileText className="w-12 h-12 text-blue-300 mx-auto mb-4" strokeWidth={1.25} />
                  <p className="overline text-blue-300 mb-2">Summit Paper</p>
                  <p className="font-serif text-xl lg:text-2xl text-white leading-snug mb-1">
                    Neurotechnology Summit 2025
                  </p>
                  <p className="text-sm text-slate-400">February 2026</p>
                </div>
              </div>

              {/* Paper description + download */}
              <div className="lg:col-span-3 p-8 lg:p-12">
                <h3 className="font-serif text-2xl text-slate-900 mb-4 leading-snug">
                  An authoritative synthesis for those working at the frontier
                </h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  The Summit was convened to examine the emerging legal, regulatory, commercial, and societal implications of neurotechnology, while also exploring practical pathways forward. It brought together a deliberately cross-sector group of participants — ensuring discussion grounded in both real-world deployment and public interest considerations.
                </p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Drawing on these contributions, the paper distils the discussion into a{" "}
                  <span className="font-medium text-slate-900">clear and decision-relevant narrative</span> for those working at the frontier of neurotechnology and its convergence with artificial intelligence.
                </p>
                <Button
                  asChild
                  size="lg"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="summit-paper-download-btn"
                >
                  <a
                    href="https://events.humanitix.com/neurotechnology-summit-2025-summit-paper"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download className="mr-2 w-4 h-4" /> Download the Summit Paper
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* Cross-sector participation chips */}
          <div className="mb-12">
            <p className="text-center text-sm uppercase tracking-wider text-slate-500 font-medium mb-6">
              Cross-sector participation
            </p>
            <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
              {[
                "Lawyers",
                "Regulators",
                "Ethicists",
                "Researchers",
                "Academics",
                "Clinicians",
                "Industry Representatives",
                "Investors",
              ].map((sector) => (
                <span
                  key={sector}
                  className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-700 shadow-sm"
                >
                  {sector}
                </span>
              ))}
            </div>
          </div>

          {/* 2x2 photo grid */}
          <div className="grid sm:grid-cols-2 gap-4 lg:gap-6">
            {[
              {
                src: "/collaborations/neurotech-summit-2025/panel-discussion.jpg",
                alt: "Panel discussion at the Neurotechnology Summit 2025 — Dr Mahendra Samarawickrama speaking into a microphone beside fellow panelists in a daylit venue",
                caption: "Cross-sector panel discussion",
                objectPosition: "center",
              },
              {
                src: "/collaborations/neurotech-summit-2025/keynote.jpg",
                alt: "Dr Mahendra Samarawickrama on stage at the Neurotechnology Summit 2025, engaging the audience during a session",
                caption: "On-stage dialogue · Summit 2025",
                objectPosition: "center 25%",
              },
              {
                src: "/collaborations/neurotech-summit-2025/audience.jpg",
                alt: "Audience and speakers engaging at the Neurotechnology Summit 2025",
                caption: "Audience engagement",
                objectPosition: "center",
              },
              {
                src: "/collaborations/neurotech-summit-2025/speakers.jpg",
                alt: "Speakers on stage at the Neurotechnology Summit 2025",
                caption: "Speakers · Neurotechnology Summit",
                objectPosition: "center 22%",
              },
            ].map((p, idx) => (
              <figure
                key={idx}
                className="rounded-xl overflow-hidden shadow-md ring-1 ring-slate-200 bg-white group"
                data-testid={`summit-photo-${idx}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    style={{ objectPosition: p.objectPosition }}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <figcaption className="text-xs text-slate-500 px-4 py-3 border-t border-slate-100">
                  {p.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>



      {/* Leadership · Landscape of Consciousness */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <p className="overline text-brand-primary mb-4">Leadership</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Shaping the Landscape of Consciousness
            </h2>
            <p className="text-slate-600 leading-relaxed">
              The Centre for Consciousness Studies is contributing to one of the most important emerging conversations in contemporary consciousness research and the philosophy of mind. Across its work, the Centre develops a research program that treats consciousness as fundamental rather than secondary, and explores how that idea connects with causation, time, physics, subjective experience, and responsible AI.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start mb-12">
            <div className="lg:col-span-3 space-y-5 text-slate-700 leading-relaxed">
              <p>
                A strong example of this wider impact is the Centre's visibility within{" "}
                <span className="font-semibold text-slate-900">Closer To Truth's Landscape of Consciousness</span>.
                Closer To Truth describes the Landscape as a global hub for theories of consciousness, authenticated by leading theorists, designed for professional consciousness communities, and open to all. The project brings together diverse approaches across consciousness studies, neuroscience, and philosophy, offering a comparative map of the field rather than a single approved view. That matters because it places the Centre's work within a serious international framework for understanding where new theories sit in relation to the broader debate.
              </p>
              <p>
                Within that Landscape, the Centre's work is highlighted through a dedicated theory page:{" "}
                <em>"Samarawickrama's Consciousness as Fundamental: A Frequency-Bearing Field,"</em>{" "}
                classified on the Landscape under{" "}
                <span className="font-semibold text-slate-900">Idealisms</span>. On the interactive{" "}
                <span className="font-semibold text-slate-900">Landscape of Consciousness map</span>, visitors can see how this theory is positioned alongside dozens of other contemporary approaches — giving a direct sense of how the Centre's ideas are situated within the wider landscape of consciousness initiatives. It also shows that the Centre's research is participating in a broader public and intellectual conversation about what consciousness is, how it relates to reality, and why it remains one of the deepest open questions in philosophy of mind.
              </p>
              <p>
                The Landscape of Consciousness project itself is a valuable public-facing resource led by the Closer To Truth program. It helps readers navigate competing theories of consciousness and understand the range of live positions in the field. For visitors new to this area, the Closer To Truth video on the Landscape offers a useful introduction to why the project was created and how it helps organise the growing diversity of consciousness theories.
              </p>
            </div>

            <div className="lg:col-span-2 space-y-6">
              <Card className="border-2 border-brand-highlight bg-white shadow-sm">
                <CardContent className="p-6 lg:p-8">
                  <div className="w-12 h-12 rounded-lg bg-brand-highlight flex items-center justify-center mb-4">
                    <Compass className="w-6 h-6 text-brand-primary" />
                  </div>
                  <p className="overline text-brand-primary mb-2">Featured Theory</p>
                  <h3 className="font-serif text-xl text-slate-900 mb-3 leading-snug">
                    Samarawickrama's Consciousness as Fundamental: A Frequency-Bearing Field
                  </h3>
                  <p className="text-sm text-slate-600 mb-5 leading-relaxed">
                    The Centre's theory page on Closer To Truth's Landscape of Consciousness — classified under{" "}
                    <span className="font-semibold text-slate-900">Idealisms</span>.
                  </p>
                  <Button
                    asChild
                    className="bg-slate-900 text-white hover:bg-slate-800 w-full sm:w-auto"
                    data-testid="landscape-theory-btn"
                  >
                    <a
                      href="https://loc.closertotruth.com/theory/samarawickrama-s-consciousness-as-fundamental-a-frequency-bearing-field"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Explore the theory page <ExternalLink className="ml-2 w-4 h-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-2 border-slate-100 bg-white shadow-sm hover:border-brand-highlight transition-colors">
                <CardContent className="p-6 lg:p-8">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                    <Workflow className="w-6 h-6 text-purple-600" />
                  </div>
                  <p className="overline text-brand-primary mb-2">Interactive Map</p>
                  <h3 className="font-serif text-xl text-slate-900 mb-3 leading-snug">
                    The Landscape of Consciousness Map
                  </h3>
                  <p className="text-sm text-slate-600 mb-5 leading-relaxed">
                    A comparative, visual map of contemporary theories of consciousness. The Centre's theory sits within the{" "}
                    <span className="font-semibold text-slate-900">Idealisms</span> region alongside other fundamental-consciousness approaches.
                  </p>
                  <Button
                    asChild
                    variant="outline"
                    className="border-slate-300 w-full sm:w-auto"
                    data-testid="landscape-map-btn"
                  >
                    <a
                      href="https://loc.closertotruth.com/map"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open the map <ExternalLink className="ml-2 w-4 h-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Closer To Truth video embed */}
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
                <Play className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="overline text-brand-primary">Suggested Viewing</p>
                <h3 className="font-serif text-xl text-slate-900 leading-snug">
                  What is the Landscape of Consciousness?
                </h3>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg ring-1 ring-slate-200 aspect-video bg-slate-900">
              <iframe
                src="https://www.youtube.com/embed/eouCYuHMe18"
                title="What is the Landscape of Consciousness? — Closer To Truth"
                className="w-full h-full"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="landscape-video-embed"
              />
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mt-4">
              This Closer To Truth video introduces the Landscape of Consciousness project and explains why mapping diverse theories of consciousness matters for the future of philosophy, science, and the study of mind.
            </p>
          </div>
        </div>
      </section>



      {/* Sustainability Focus */}
      <section className="py-16 lg:py-24 bg-white" data-testid="sustainability-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <p className="overline text-brand-primary mb-4">Sustainability Focus</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Innovation that Strengthens Society
            </h2>
            <p className="text-slate-600 leading-relaxed mb-5">
              Our sustainability focus is grounded in the belief that innovation should strengthen society, not just accelerate technology. Across our initiatives, we work to ensure that advances in consciousness studies, responsible AI, human-centred design, and emerging technologies contribute to a more ethical, inclusive, and accountable future.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Our commitment aligns with the United Nations Sustainable Development Goals: four <span className="font-semibold text-slate-900">primary alignments</span> — SDG 9, SDG 16, SDG 10, and SDG 17 — anchor our work, with <span className="font-semibold text-slate-900">further contributions</span> across SDG 4, SDG 3, SDG 8, and SDG 11. Together, these goals reflect our broader mission to shape innovation in ways that are socially responsible, governance-ready, and oriented toward long-term public benefit.
            </p>
          </div>

          {/* Primary SDG alignments */}
          <div className="max-w-3xl mx-auto text-center mb-8">
            <p className="overline text-brand-primary mb-3">Primary Alignments</p>
            <h3 className="font-serif text-2xl lg:text-3xl text-slate-900 mb-3">
              The Four SDGs at the Centre of Our Work
            </h3>
            <p className="text-slate-600 leading-relaxed">
              These four goals define the core of our sustainability agenda — shaping how we design research, governance, and partnerships for responsible innovation.
            </p>
          </div>

          {/* Four SDG cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            <Card className="border-2 border-slate-100 hover:border-brand-highlight transition-colors h-full">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-7 h-7 text-amber-600" />
                  </div>
                  <div>
                    <p className="overline text-amber-700 mb-1">SDG 9</p>
                    <h3 className="font-serif text-xl text-slate-900 leading-snug">
                      Industry, Innovation and Infrastructure
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  We support innovation that is not only technically advanced, but also safe, human-centred, and built for real-world impact. Our work explores how responsible AI, critical infrastructure, neurotechnology, and emerging systems can be developed with stronger ethical foundations from the start. This means helping shape future-facing technologies that are more trustworthy, resilient, and better aligned with human needs.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-slate-100 hover:border-brand-highlight transition-colors h-full">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Scale className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <p className="overline text-blue-700 mb-1">SDG 16</p>
                    <h3 className="font-serif text-xl text-slate-900 leading-snug">
                      Peace, Justice and Strong Institutions
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  We place governance, accountability, and human oversight at the centre of technological progress. Our research highlights the importance of transparency, explainability, and meaningful human responsibility in the design and use of advanced AI systems. In a world where technology is increasingly involved in decisions that affect people's lives, strong institutions and ethical governance are essential to maintaining public trust and protecting human dignity.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-slate-100 hover:border-brand-highlight transition-colors h-full">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
                    <Users2 className="w-7 h-7 text-rose-600" />
                  </div>
                  <div>
                    <p className="overline text-rose-700 mb-1">SDG 10</p>
                    <h3 className="font-serif text-xl text-slate-900 leading-snug">
                      Reduced Inequalities
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  We recognise that innovation can deepen inequality unless it is designed with inclusion in mind. That is why our sustainability approach emphasises diversity, equity, inclusion, and bias awareness across research, governance, and implementation. We are committed to helping create systems and frameworks that do not exclude people, but instead broaden participation, reduce harm, and support more equitable outcomes across communities.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-slate-100 hover:border-brand-highlight transition-colors h-full">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Handshake className="w-7 h-7 text-emerald-600" />
                  </div>
                  <div>
                    <p className="overline text-emerald-700 mb-1">SDG 17</p>
                    <h3 className="font-serif text-xl text-slate-900 leading-snug">
                      Partnerships for the Goals
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Lasting impact depends on collaboration. Our work is shaped by the view that progress in AI, ethics, governance, and sustainability must be built through partnerships across disciplines, sectors, and communities. By encouraging collaboration between researchers, institutions, industry, and the wider public, we aim to contribute to shared knowledge, stronger governance models, and more inclusive pathways for responsible innovation.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Secondary SDG alignments */}
          <div className="max-w-3xl mx-auto text-center mb-8">
            <p className="overline text-brand-primary mb-3">Further SDG Alignment</p>
            <h3 className="font-serif text-2xl lg:text-3xl text-slate-900 mb-3">
              Connected Goals Across Education, Health, Work, and Communities
            </h3>
            <p className="text-slate-600 leading-relaxed">
              Our work also contributes meaningfully across these four supporting SDGs — recognising that responsible innovation touches every part of how people learn, live, work, and belong.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <Card className="border-2 border-slate-100 hover:border-brand-highlight transition-colors h-full">
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="overline text-purple-700 mb-1">SDG 4</p>
                    <h3 className="font-serif text-lg text-slate-900 leading-snug">
                      Quality Education
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  We see education as a vital part of responsible innovation. Our work contributes to public understanding, interdisciplinary learning, and the development of knowledge frameworks that help people engage critically and responsibly with complex technologies. By supporting education in ethics, governance, human-centred design, and emerging technologies, we aim to strengthen the foundations for informed and socially responsible innovation.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-slate-100 hover:border-brand-highlight transition-colors h-full">
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-pink-100 flex items-center justify-center flex-shrink-0">
                    <HeartPulse className="w-6 h-6 text-pink-600" />
                  </div>
                  <div>
                    <p className="overline text-pink-700 mb-1">SDG 3</p>
                    <h3 className="font-serif text-lg text-slate-900 leading-snug">
                      Good Health and Well-being
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Our work also connects to human well-being through its engagement with neurotechnology, brain-computer interfaces, human factors, and the broader ethical design of technologies that affect people's lives. We are interested in approaches that protect safety, support human flourishing, and ensure that technological systems are developed with care for wellbeing, dignity, and the integrity of human experience.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-slate-100 hover:border-brand-highlight transition-colors h-full">
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <p className="overline text-cyan-700 mb-1">SDG 8</p>
                    <h3 className="font-serif text-lg text-slate-900 leading-snug">
                      Decent Work and Economic Growth
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  We support innovation that contributes to sustainable prosperity while remaining grounded in ethics and human responsibility. As AI continues to reshape work, institutions, and economic systems, we believe it is essential to guide these changes in ways that strengthen accountability, support human capability, and promote sustainable and socially responsible growth.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-slate-100 hover:border-brand-highlight transition-colors h-full">
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Landmark className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="overline text-orange-700 mb-1">SDG 11</p>
                    <h3 className="font-serif text-lg text-slate-900 leading-snug">
                      Sustainable Cities and Communities
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  We are committed to approaches that support safer, more resilient, and more inclusive communities. Our work on responsible AI, governance, and critical infrastructure speaks directly to the systems that shape daily life, including public services, urban technologies, community trust, and decision-making environments. By promoting human-centred oversight, transparency, and long-term responsibility in complex socio-technical systems, we aim to contribute to communities that are not only more technologically capable, but also more sustainable, equitable, and secure.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sustainability in action — speaking engagements gallery */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-brand-highlight flex items-center justify-center flex-shrink-0">
                <Leaf className="w-5 h-5 text-brand-primary" />
              </div>
              <div>
                <p className="overline text-brand-primary">Sustainability in Action</p>
                <h3 className="font-serif text-xl lg:text-2xl text-slate-900 leading-snug">
                  Convening cross-sector dialogue on responsible innovation
                </h3>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed max-w-4xl mb-8">
              The Centre's leadership regularly contributes to global summits, policy forums, and industry conferences — translating the SDG commitments above into practical guidance for governance, ethics, safety, and sustainable development across AI, neurotechnology, and quantum computing.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <figure className="rounded-xl overflow-hidden ring-1 ring-slate-200 shadow-sm bg-white group">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src="/sustainability/iso-iec-bci-symposium.jpg"
                    alt="ISO/IEC JTC 1/SC 43 Symposium on Brain-Computer Interface — international standards dialogue"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <figcaption className="p-4">
                  <p className="text-sm font-semibold text-slate-900">ISO/IEC JTC 1/SC 43 · Symposium on Brain-Computer Interface</p>
                  <p className="text-xs text-slate-500 mt-1">Contributing to international standards dialogue on safety, ethics, and interoperability in BCI</p>
                </figcaption>
              </figure>

              <figure className="rounded-xl overflow-hidden ring-1 ring-slate-200 shadow-sm bg-white group">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src="/sustainability/talk-genai-summit.jpg"
                    alt="Keynote on the convergence of brain and deep-learning AI at the Generative AI Summit"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <figcaption className="p-4">
                  <p className="text-sm font-semibold text-slate-900">Generative AI Summit · Generative AI for Education Leaders</p>
                  <p className="text-xs text-slate-500 mt-1">Keynote on brain-inspired AI, ethics, and human-centred design</p>
                </figcaption>
              </figure>

              <figure className="rounded-xl overflow-hidden ring-1 ring-slate-200 shadow-sm bg-white group">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src="/sustainability/panel-cio50.jpg"
                    alt="Panel discussion at the CIO50 Symposium & Awards, ICC Sydney"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <figcaption className="p-4">
                  <p className="text-sm font-semibold text-slate-900">CIO50 Symposium &amp; Awards · ICC Sydney</p>
                  <p className="text-xs text-slate-500 mt-1">Industry panel on responsible AI, governance, and digital leadership</p>
                </figcaption>
              </figure>

              <figure className="rounded-xl overflow-hidden ring-1 ring-slate-200 shadow-sm bg-white group">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src="/sustainability/talk-datagov-2024.jpg"
                    alt="Presentation at DataGov 2024, Canberra: VR, AI, and Neurotech Convergence"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <figcaption className="p-4">
                  <p className="text-sm font-semibold text-slate-900">DataGov 2024 · Canberra</p>
                  <p className="text-xs text-slate-500 mt-1">"The Triad of Tech: VR, AI, and Neurotech Convergence" — Future of Government Data forum</p>
                </figcaption>
              </figure>

              <figure className="rounded-xl overflow-hidden ring-1 ring-slate-200 shadow-sm bg-white group">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src="/sustainability/talk-ai-ethics-governance.jpg"
                    alt="Keynote on AI ethics and governance, exploring brain and deep-learning architectures"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <figcaption className="p-4">
                  <p className="text-sm font-semibold text-slate-900">Industry Keynote · AI Ethics &amp; Governance</p>
                  <p className="text-xs text-slate-500 mt-1">"Headed Towards AI Ethics and Governance" — brain-inspired models meet responsible AI</p>
                </figcaption>
              </figure>

              <figure className="rounded-xl overflow-hidden ring-1 ring-slate-200 shadow-sm bg-white group">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src="/sustainability/talk-quantum-factor.jpg"
                    alt="Keynote on quantum computing, superposition, interference and entanglement at Factor Insights"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <figcaption className="p-4">
                  <p className="text-sm font-semibold text-slate-900">Factor Insights · Security Day · Quantum Computing</p>
                  <p className="text-xs text-slate-500 mt-1">Keynote on quantum-native intelligence, superposition, entanglement, and the human-centred governance of advanced computation</p>
                </figcaption>
              </figure>
            </div>
          </div>

          {/* Closing vision */}
          <div className="bg-slate-50 rounded-2xl p-8 lg:p-12 border border-slate-200">
            <div className="max-w-4xl mx-auto">
              <p className="overline text-brand-primary mb-3 text-center">Our Broader Sustainability Vision</p>
              <h3 className="font-serif text-2xl lg:text-3xl text-slate-900 mb-4 text-center leading-snug">
                Sustainability as a guiding principle, not an afterthought
              </h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Sustainability, for us, is not a separate layer added after innovation. It is a guiding principle that shapes how innovation is imagined, designed, governed, and applied. We see responsible research and technology development as part of a larger commitment to social and environmental responsibility, human values, and the public good.
              </p>
              <p className="text-slate-700 leading-relaxed">
                By aligning our initiatives with these primary UN Sustainable Development Goals, we aim to contribute to a future in which innovation supports fairness, accountability, inclusion, and meaningful collaboration.
              </p>
            </div>
          </div>
        </div>
      </section>



      {/* Call to Action */}
      <section className="py-16 lg:py-24 bg-white" data-testid="transformation-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 rounded-2xl shadow-sm border border-slate-200 p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-serif text-3xl text-slate-900 mb-4">
                  Join the Conversation
                </h2>
                <p className="text-slate-600 mb-6">
                  The questions raised by this global transformation require diverse perspectives and collaborative inquiry. We welcome engagement from researchers, policymakers, industry leaders, and thoughtful citizens.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
                <Button
                  asChild
                  size="lg"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="explore-research-btn"
                >
                  <Link to="/research">
                    Explore Our Research <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="border-slate-300"
                  data-testid="contact-btn"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ImpactPage;
